<script setup lang="ts">
import axios from 'axios'
import { Download, Loader2 } from 'lucide-vue-next'

import useNotification from '@/composables/notification'
import useOffer from '@/composables/offer'
import { getApiErrorMessage } from '@/utils/apiError.js'

import OfferPromotionalCodeList from './OfferPromotionalCodeList.vue'

import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import type { OfferGeneratePromotionalCodesPayload, PromotionalCodeGenerationType } from '@/types/types'

const props = defineProps<{
  offerId: number
  isPromotionalCodeRequired: boolean
}>()

const promotionalCodeListRef = useTemplateRef('promotionalCodeListRef')

const { t } = useI18n()
const notification = useNotification()
const { generatePromotionalCodes } = useOffer()
const { user } = useLocalStorage()

const generationType = ref<PromotionalCodeGenerationType>('global')
const slug = ref('')
const usageLimit = ref<string | number>('')
const slugTouched = ref(false)
const usageLimitTouched = ref(false)
const isSubmitting = ref(false)
const isModalOpen = ref(false)
const isExportingCsv = ref(false)

const slugPattern = /^[A-Za-z0-9]+$/

const exportPromotionalCodesCsv = async () => {
  if (isExportingCsv.value) return
  if (!user.value?.merchant?.id) return

  isExportingCsv.value = true

  try {
    const { data } = await axios.get(
      `/merchants/${user.value.merchant.id}/offers/${props.offerId}/promotional-codes/export`,
      { responseType: 'blob' }
    )

    const fileURL = window.URL.createObjectURL(new Blob([data]))
    const fileLink = document.createElement('a')
    fileLink.href = fileURL
    fileLink.setAttribute('download', `promo-codes-offer-${props.offerId}.csv`)
    document.body.appendChild(fileLink)
    fileLink.click()
    fileLink.remove()

    notification.create('success', {
      text: t('global.download.success'),
      timeout: 2000
    })
  } catch (error: unknown) {
    notification.create('error', {
      text: getApiErrorMessage(error) ?? t('global.download.error'),
      persist: true
    })
  } finally {
    isExportingCsv.value = false
  }
}

const options = computed<(RadioCardOption & { id: PromotionalCodeGenerationType })[]>(() => [
  {
    id: 'global',
    title: t('pages.offer.detail.promotionalCodeSettings.generationTypeGlobal'),
    description: t('pages.offer.detail.promotionalCodeSettings.generationTypeGlobalDesc')
  },
  {
    id: 'unique',
    title: t('pages.offer.detail.promotionalCodeSettings.generationTypeUnique'),
    description: t('pages.offer.detail.promotionalCodeSettings.generationTypeUniqueDesc')
  }
])

const selectedOption = computed({
  get: (): RadioCardOption & { id: PromotionalCodeGenerationType } => {
    const found = options.value.find((option) => option.id === generationType.value)
    if (found) return found

    const first = options.value[0]
    if (first) return first

    return {
      id: 'global' as PromotionalCodeGenerationType,
      title: t('pages.offer.detail.promotionalCodeSettings.generationTypeGlobal'),
      description: t('pages.offer.detail.promotionalCodeSettings.generationTypeGlobalDesc')
    }
  },
  set: (option: RadioCardOption & { id: PromotionalCodeGenerationType }) => {
    generationType.value = option.id
  }
})

const usageLimitText = computed(() => String(usageLimit.value ?? '').trim())

const parsedUsageLimit = computed(() => Number.parseInt(usageLimitText.value, 10))

const slugError = computed(() => {
  const trimmedSlug = slug.value.trim()

  if (!trimmedSlug) {
    return t('pages.offer.detail.promotionalCodeSettings.slugRequired')
  }

  if (!slugPattern.test(trimmedSlug)) {
    return t('pages.offer.detail.promotionalCodeSettings.slugInvalid')
  }

  return null
})

const usageLimitError = computed(() => {
  if (!usageLimitText.value) {
    return selectedOption.value.id === 'global'
      ? t('pages.offer.detail.promotionalCodeSettings.usageLimitRequired')
      : t('pages.offer.detail.promotionalCodeSettings.quantityRequired')
  }

  if (!Number.isInteger(parsedUsageLimit.value) || parsedUsageLimit.value < 1) {
    return selectedOption.value.id === 'global'
      ? t('pages.offer.detail.promotionalCodeSettings.usageLimitMin')
      : t('pages.offer.detail.promotionalCodeSettings.quantityMin')
  }

  return null
})

const showSlugError = computed(() => slugTouched.value && !!slugError.value)
const showUsageLimitError = computed(() => usageLimitTouched.value && !!usageLimitError.value)

const isFormDisabled = computed(() => !props.isPromotionalCodeRequired || isSubmitting.value)

const handleGenerate = async () => {
  if (isFormDisabled.value || slugError.value || usageLimitError.value) {
    return
  }

  isSubmitting.value = true

  try {
    const payload: OfferGeneratePromotionalCodesPayload = {
      slug: slug.value.trim(),
      generationType: generationType.value,
      // Backend ignores `usage_limit` for `unique` generation, but the payload type requires a number.
      usageLimit: parsedUsageLimit.value
    }

    if (generationType.value === 'unique') {
      payload.quantity = parsedUsageLimit.value
    }

    await generatePromotionalCodes(props.offerId, payload)

    notification.create('success', {
      text: t('pages.offer.detail.promotionalCodeSettings.success')
    })
    isModalOpen.value = false
    promotionalCodeListRef.value?.refresh()
  } catch (error: unknown) {
    notification.create('error', {
      text: getApiErrorMessage(error) ?? t('pages.offer.detail.promotionalCodeSettings.error')
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <Card class="pb-0">
    <CardHeader v-if="isPromotionalCodeRequired">
      <div class="flex items-start justify-between gap-4 w-full">
        <div>
          <CardTitle>{{ t('pages.offer.detail.promotionalCodeSettings.title') }}</CardTitle>
          <CardDescription>{{ t('pages.offer.detail.promotionalCodeSettings.description') }}</CardDescription>
        </div>

        <Button variant="outline" size="icon" :disabled="isExportingCsv" @click="exportPromotionalCodesCsv">
          <Loader2 v-if="isExportingCsv" class="h-4 w-4 animate-spin" />
          <Download v-else class="h-4 w-4" />
        </Button>
      </div>
    </CardHeader>

    <CardContent :class="isPromotionalCodeRequired ? 'space-y-5' : undefined">
      <template v-if="isPromotionalCodeRequired">
        <Button variant="default-primary" @click="isModalOpen = true">
          {{ t('pages.offer.detail.promotionalCodeSettings.openButton') }}
        </Button>

        <div class="-mx-6">
          <OfferPromotionalCodeList ref="promotionalCodeListRef" :offer-id="offerId" embedded />
        </div>
      </template>

      <p v-else class="text-sm text-muted-foreground">
        {{ t('pages.offer.detail.promotionalCodeSettings.notRequiredHint') }}
      </p>
    </CardContent>
  </Card>

  <BaseModal
    :is-open="isModalOpen"
    :heading-title="t('pages.offer.detail.promotionalCodeSettings.title')"
    size="2xl"
    :show-footer="false"
    @close="isModalOpen = false"
  >
    <fieldset :disabled="isFormDisabled" class="space-y-5">
      <div class="w-full flex flex-col gap-3">
        <AppRadioCard v-model="selectedOption" :options="options" class="w-full" />
      </div>

      <div>
        <label for="promotional-code-slug" class="mb-1.5 block text-sm font-medium text-foreground">
          {{
            selectedOption.id === 'global'
              ? t('pages.offer.detail.promotionalCodeSettings.slugLabel')
              : t('pages.offer.detail.promotionalCodeSettings.slugPrefixLabel')
          }}
        </label>
        <Input
          id="promotional-code-slug"
          v-model="slug"
          :placeholder="t('pages.offer.detail.promotionalCodeSettings.slugPlaceholder')"
          :aria-invalid="showSlugError || undefined"
          class="w-full"
          @blur="slugTouched = true"
        />
        <p class="mt-1 text-xs text-muted-foreground">
          {{ t('pages.offer.detail.promotionalCodeSettings.slugHint') }}
        </p>
        <p v-if="showSlugError" class="mt-1 text-xs text-red-500">
          {{ slugError }}
        </p>
      </div>

      <div>
        <label for="promotional-code-usage-limit" class="mb-1.5 block text-sm font-medium text-foreground">
          {{
            selectedOption.id === 'global'
              ? t('pages.offer.detail.promotionalCodeSettings.usageLimitLabel')
              : t('pages.offer.detail.promotionalCodeSettings.quantityLabel')
          }}
        </label>
        <Input
          id="promotional-code-usage-limit"
          v-model="usageLimit"
          type="number"
          min="1"
          :placeholder="
            selectedOption.id === 'global'
              ? t('pages.offer.detail.promotionalCodeSettings.usageLimitPlaceholder')
              : t('pages.offer.detail.promotionalCodeSettings.quantityPlaceholder')
          "
          :aria-invalid="showUsageLimitError || undefined"
          class="w-full"
          @blur="usageLimitTouched = true"
        />
        <p v-if="showUsageLimitError" class="mt-1 text-xs text-red-500">
          {{ usageLimitError }}
        </p>
      </div>

      <Button
        variant="default-primary"
        class="w-full sm:w-auto"
        :disabled="isFormDisabled || !!slugError || !!usageLimitError"
        @click="handleGenerate"
      >
        <Loader2 v-if="isSubmitting" class="animate-spin" />
        <template v-else>
          {{ t('pages.offer.detail.promotionalCodeSettings.generateButton') }}
        </template>
      </Button>
    </fieldset>
  </BaseModal>
</template>
